<template>
  <div
    id="similar-rvs"
    class="bg-gray-50"
  >
    <TContainer class="py-16 lg:py-24 space-y-2">
      <THeading
        :level="3"
        tag="h3"
      >
        {{ t('similarRvs') }}
      </THeading>

      <LazyTBreadcrumb
        v-if="breadcrumbList.length && !isWebView"
        :items="breadcrumbList"
        target="_blank"
        rel="noopener noreferrer"
        class="mb-4"
      />

      <GeneralSimilarRvs
        :rv-id="rvId"
        :alias-name="rvAliasName ?? undefined"
      />
    </TContainer>
  </div>
</template>

<script setup lang="ts">
import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import { ROUTE_BASE_NAME } from '~/constants'
import { POI_RENTAL_TYPE_KEYWORDS_EN_TO_FR, SUPPORTED_RV_TYPES_TO_POI_KEYWORD } from '~/constants/keywords'
import type { PointOfInterestRouteDetails } from '~/types/rental-api-aliases'

const props = defineProps<{
  destination?: PointOfInterestRouteDetails
}>()

const {
  rvAliasName,
  rvId,
  rvRVType,
} = useRvDetails()

const { locale, t, te } = useI18n()
const localePath = useLocalePath()
const { isWebView } = usePlatform()

const breadcrumbList = computed(() => {
  if (!props.destination?.Breadcrumbs) {
    return []
  }

  const base: Breadcrumb[] = [
    {
      text: t('pageLink.home'),
      to: localePath({
        name: 'index',
      }),
      active: true,
    },
    {
      text: t('pageLink.rvRentals'),
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals,
      }),
      active: true,
    },
  ]

  const englishPoiKeyword = rvRVType.value
    ? SUPPORTED_RV_TYPES_TO_POI_KEYWORD[rvRVType.value as keyof typeof SUPPORTED_RV_TYPES_TO_POI_KEYWORD]
    : undefined

  if (englishPoiKeyword) {
    const localizedKeywordKey = locale.value === 'fr'
      ? POI_RENTAL_TYPE_KEYWORDS_EN_TO_FR[englishPoiKeyword]
      : englishPoiKeyword

    const localizedKeywordSlug = te(`keyword['${localizedKeywordKey}'].slug.${locale.value}`)
      ? t(`keyword['${localizedKeywordKey}'].slug.${locale.value}`)
      : localizedKeywordKey

    const label = te(`keyword['${localizedKeywordKey}'].label_enhanced`)
      ? formatAsTitleCase(t(`keyword['${localizedKeywordKey}'].label_enhanced`))
      : formatAsTitleCase(t(`keyword['${localizedKeywordKey}'].label`))

    base.push({
      text: label,
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_keyword,
        params: {
          keyword: localizedKeywordSlug,
        },
      }),
      active: true,
    })
  }

  props.destination.Breadcrumbs.forEach((breadcrumb, index) => {
    base.push({
      text: breadcrumb.Names?.[locale.value] ?? '',
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_destination,
        params: {
          destinationPath: destinationPathSegments(breadcrumb.CanonicalPaths?.[locale.value]),
        },
      }),
      active: index + 1 < (props.destination?.Breadcrumbs?.length ?? 0),
    })
  })

  return base
})
</script>

<i18n src="~~/i18n/locales/common/search/keywords.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "similarRvs": "Similar RVs"
  },
  "fr": {
    "similarRvs": "Jeter un coup d’oeil à ces VR semblables"
  }
}
</i18n>
